import { useState, type KeyboardEvent } from "react";
import { ExternalLink, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { coerceValue, type ProspectFieldDef } from "@/lib/prospectFields";
import { editorText, formatProspectValue, optionTone } from "@/lib/prospectDisplay";
import { safeHttpUrl } from "@/lib/safeUrl";
import { cn } from "@/lib/utils";

/**
 * Célula de lead frio no estilo planilha: mostra o valor formatado pelo tipo
 * da coluna e abre um editor em popover (texto, número pt-BR, data, seleção,
 * sim/não, avaliação…). Valida com `coerceValue` — o mesmo módulo da API/MCP —
 * e entrega o valor canônico em `onSave` (`null` = limpar). Não grava nada:
 * o pai chama o hook de mutation.
 */
export interface ProspectCellProps {
  field: Pick<ProspectFieldDef, "type" | "options" | "label">;
  value: unknown;
  onSave: (value: unknown) => void;
  disabled?: boolean;
  /** Linha (grade) ou bloco (painel lateral). */
  variant?: "cell" | "block";
  className?: string;
}

export function ProspectCell({
  field,
  value,
  onSave,
  disabled = false,
  variant = "cell",
  className,
}: ProspectCellProps) {
  const [open, setOpen] = useState(false);
  const empty = value === null || value === undefined || value === "";
  const href = field.type === "url" && typeof value === "string" ? safeHttpUrl(value) : null;

  const save = (next: unknown) => {
    onSave(next);
    setOpen(false);
  };

  const display = (() => {
    if (empty) return <span className="text-muted-foreground/60">—</span>;
    if (field.type === "select") {
      return (
        <StatusBadge shape="tag" tone={optionTone(field, String(value))}>
          {formatProspectValue(field, value)}
        </StatusBadge>
      );
    }
    if (field.type === "multi_select" && Array.isArray(value)) {
      return (
        <span className="flex flex-wrap gap-1">
          {value.map((v) => (
            <StatusBadge key={String(v)} shape="tag" tone={optionTone(field, String(v))}>
              {formatProspectValue({ ...field, type: "select" }, v)}
            </StatusBadge>
          ))}
        </span>
      );
    }
    return (
      <span
        className={cn(
          variant === "cell" ? "block truncate" : "whitespace-pre-wrap break-words",
          (field.type === "number" || field.type === "currency") && "tabular-nums",
        )}
      >
        {formatProspectValue(field, value)}
      </span>
    );
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div
        className={cn(
          "group/cell flex min-w-0 items-center gap-1",
          variant === "cell" ? "max-w-[320px]" : "w-full",
          className,
        )}
      >
        {href ? (
          <>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-w-0 items-center gap-1 truncate text-primary hover:underline"
            >
              <span className="truncate">{formatProspectValue(field, value)}</span>
              <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
            </a>
            {!disabled && (
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label={`Editar ${field.label}`}
                  className="shrink-0 rounded p-1 text-muted-foreground opacity-0 hover:bg-muted focus-visible:opacity-100 group-hover/cell:opacity-100"
                >
                  <Pencil className="h-3 w-3" aria-hidden="true" />
                </button>
              </PopoverTrigger>
            )}
          </>
        ) : disabled ? (
          <div className="min-w-0">{display}</div>
        ) : (
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`${field.label}: ${empty ? "vazio" : formatProspectValue(field, value)} — editar`}
              className={cn(
                "min-h-[28px] w-full min-w-0 rounded-md px-1.5 py-1 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                variant === "block" && "border border-border px-2.5 py-2",
              )}
            >
              {display}
            </button>
          </PopoverTrigger>
        )}
      </div>
      <PopoverContent align="start" className="w-[300px] p-3">
        {open && (
          <CellEditor field={field} value={value} onSave={save} onCancel={() => setOpen(false)} />
        )}
      </PopoverContent>
    </Popover>
  );
}

interface CellEditorProps {
  field: ProspectCellProps["field"];
  value: unknown;
  onSave: (value: unknown) => void;
  onCancel: () => void;
}

function CellEditor({ field, value, onSave, onCancel }: CellEditorProps) {
  const [text, setText] = useState(() => editorText(field.type, value));
  const [error, setError] = useState<string | null>(null);
  const [multi, setMulti] = useState<string[]>(() =>
    Array.isArray(value) ? value.map(String) : [],
  );

  const commitText = () => {
    const result = coerceValue(field, text);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    onSave(result.value);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !(field.type === "long_text" && !e.metaKey && !e.ctrlKey)) {
      e.preventDefault();
      commitText();
    }
    if (e.key === "Escape") onCancel();
  };

  const title = (
    <div className="mb-2 text-[11px] font-bold uppercase text-muted-foreground">{field.label}</div>
  );
  const clear = (
    <Button type="button" variant="ghost" size="sm" onClick={() => onSave(null)}>
      Limpar
    </Button>
  );

  if (field.type === "select") {
    return (
      <div>
        {title}
        <div className="flex max-h-[240px] flex-col gap-1 overflow-y-auto">
          {field.options.length === 0 && (
            <p className="text-xs text-muted-foreground">
              Sem opções — adicione no menu da coluna.
            </p>
          )}
          {field.options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => onSave(o.value)}
              className={cn(
                "flex items-center rounded-md px-2 py-1.5 text-left hover:bg-muted",
                o.value === value && "bg-muted",
              )}
            >
              <StatusBadge shape="tag" tone={o.tone ?? "neutral"}>
                {o.label}
              </StatusBadge>
            </button>
          ))}
        </div>
        <div className="mt-2 flex justify-end">{clear}</div>
      </div>
    );
  }

  if (field.type === "multi_select") {
    return (
      <div>
        {title}
        <div className="flex max-h-[240px] flex-col gap-1 overflow-y-auto">
          {field.options.map((o) => {
            const id = `opt-${o.value.replace(/\W+/g, "-")}`;
            return (
              <div
                key={o.value}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted"
              >
                <Checkbox
                  id={id}
                  checked={multi.includes(o.value)}
                  onCheckedChange={(v) =>
                    setMulti((cur) =>
                      v === true ? [...cur, o.value] : cur.filter((x) => x !== o.value),
                    )
                  }
                />
                <label htmlFor={id} className="cursor-pointer text-sm">
                  {o.label}
                </label>
              </div>
            );
          })}
        </div>
        <div className="mt-2 flex justify-end gap-2">
          {clear}
          <Button type="button" size="sm" onClick={() => onSave(multi.length ? multi : null)}>
            Salvar
          </Button>
        </div>
      </div>
    );
  }

  if (field.type === "boolean") {
    return (
      <div>
        {title}
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant={value === true ? "default" : "outline"}
            onClick={() => onSave(true)}
          >
            Sim
          </Button>
          <Button
            type="button"
            size="sm"
            variant={value === false ? "default" : "outline"}
            onClick={() => onSave(false)}
          >
            Não
          </Button>
          {clear}
        </div>
      </div>
    );
  }

  if (field.type === "rating") {
    return (
      <div>
        {title}
        <div className="flex gap-1" role="group" aria-label="Avaliação">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              aria-label={`${n} de 5`}
              onClick={() => onSave(n)}
              className={cn(
                "h-9 w-9 rounded-md text-lg hover:bg-muted",
                Number(value) >= n ? "text-warn" : "text-muted-foreground",
              )}
            >
              ★
            </button>
          ))}
        </div>
        <div className="mt-2 flex justify-end">{clear}</div>
      </div>
    );
  }

  const inputId = "prospect-cell-editor";
  return (
    <div>
      <label
        htmlFor={inputId}
        className="mb-2 block text-[11px] font-bold uppercase text-muted-foreground"
      >
        {field.label}
      </label>
      {field.type === "long_text" ? (
        <Textarea
          id={inputId}
          autoFocus
          rows={5}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setError(null);
          }}
          onKeyDown={onKeyDown}
        />
      ) : (
        <Input
          id={inputId}
          autoFocus
          type={field.type === "date" ? "date" : "text"}
          inputMode={field.type === "number" || field.type === "currency" ? "decimal" : undefined}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setError(null);
          }}
          onKeyDown={onKeyDown}
        />
      )}
      {error && (
        <p role="alert" className="mt-1.5 text-xs font-semibold text-bad">
          {error}
        </p>
      )}
      <div className="mt-2 flex justify-end gap-2">
        {clear}
        <Button type="button" size="sm" onClick={commitText}>
          Salvar
        </Button>
      </div>
    </div>
  );
}
